import { ChevronLeft, DoorOpen, MapPin, Navigation, Phone } from 'lucide-react';
import { zonedToday, type PublicLocation, type WalkInDay } from '@rx-manager/shared';
import { useI18n } from '../../lib/i18n';
import { Button, IconTile, StepActions, buttonClasses, cn } from '../../components/ui';

/**
 * Track BCW (BCW-D6, D7) — a service patients walk in for: no time, no hold, nothing stored. The
 * page says when each branch takes walk-ins, today first, with the address and a way to call.
 * Checking in and the queue are the separate Check-in and queue item.
 */

/** `09:00–12:00, 13:00–17:00`. */
export function windowsText(day: WalkInDay | undefined): string {
  return (day?.windows ?? []).map((w) => `${w.start}–${w.end}`).join(', ');
}

/** Today's walk-in day at a branch, if it takes walk-ins today. */
export function walkInToday(location: PublicLocation): WalkInDay | undefined {
  const today = zonedToday(location.timezone);
  return location.walkInHours?.find((day) => day.date === today);
}

const dayLabel = (date: string, locale: string): string =>
  new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));

const mapsUrl = (address: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

export function WalkInStep({
  locations,
  phone,
  onBack,
}: {
  locations: PublicLocation[];
  /** The pharmacy's number, for a branch with none of its own. */
  phone: string | null;
  onBack: () => void;
}) {
  const { t, locale } = useI18n();
  return (
    <div className="space-y-5">
      <p className="text-base text-foreground">{t('walkin.lead')}</p>
      {locations.map((location) => {
        const today = walkInToday(location);
        const days = location.walkInHours ?? [];
        const call = location.phone ?? phone;
        return (
          <section key={location.id} className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="flex items-start gap-4 bg-accent/60 p-4 sm:p-5">
              <IconTile icon={DoorOpen} className="bg-card" />
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-heading">
                  {t('walkin.hoursFor', { location: location.name })}
                </h2>
                <p className="mt-1 text-base font-medium text-heading">
                  {t('walkin.today')}: {today ? windowsText(today) : t('walkin.closedToday')}
                </p>
              </div>
            </div>
            <div className="space-y-4 p-4 sm:p-5">
              {days.length === 0 ? (
                <p className="text-base text-muted-foreground">{t('walkin.none')}</p>
              ) : (
                <ul className="divide-y divide-border text-base text-foreground">
                  {days.map((day) => (
                    <li
                      key={day.date}
                      className={cn(
                        'flex flex-wrap justify-between gap-x-4 py-2',
                        day.date === today?.date && 'font-semibold text-heading',
                      )}
                    >
                      <span className="capitalize">{dayLabel(day.date, locale)}</span>
                      <span className="tabular-nums">{windowsText(day)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {location.address && (
                <p className="flex items-start gap-2 text-base text-muted-foreground">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {location.address}
                </p>
              )}
              <div className="flex flex-wrap gap-3">
                {location.address && (
                  <a
                    href={mapsUrl(location.address)}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={buttonClasses('outline', 'sm')}
                  >
                    <Navigation className="h-4 w-4" aria-hidden="true" />
                    {t('walkin.directions')}
                  </a>
                )}
                {call && (
                  <a href={`tel:${call.replace(/[^\d+]/g, '')}`} className={buttonClasses('outline', 'sm')}>
                    <Phone className="h-4 w-4" aria-hidden="true" />
                    {t('walkin.call', { phone: call })}
                  </a>
                )}
              </div>
            </div>
          </section>
        );
      })}
      <StepActions>
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {t('step.back')}
        </Button>
      </StepActions>
    </div>
  );
}

/** "Or walk in today: 09:00–17:00." above the times of a book-or-walk-in service. */
export function WalkInNote({ location }: { location: PublicLocation }) {
  const { t } = useI18n();
  const today = walkInToday(location);
  if (!today || today.windows.length === 0) return null;
  return (
    <p className="flex items-center gap-2.5 rounded-xl border border-primary/20 bg-accent/60 px-4 py-3 text-base text-heading">
      <DoorOpen className="h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
      {t('walkin.orWalkIn', { hours: windowsText(today) })}
    </p>
  );
}
